import Dexie, { type EntityTable, type Table } from "dexie";
import type { ChatSession, Project, WorkspaceFile, WorkspaceScope } from "../types";
import { normalizeProjectPath } from "./path";
import { DEFAULT_MODEL, DEFAULT_REASONING_EFFORT } from "../../shared/models";

export type LegacyWorkspaceFile = Omit<WorkspaceFile, "chatId">;

function createChatRecord(projectId: string, model = DEFAULT_MODEL, now = Date.now()): ChatSession {
  return { id: crypto.randomUUID(), projectId, title: "Nouvelle tâche", model, effort: DEFAULT_REASONING_EFFORT,
    messages: [], apiInput: [], createdAt: now, updatedAt: now };
}

function sameSnapshot(file: LegacyWorkspaceFile, snapshot: NonNullable<ChatSession["messages"][number]["changes"]>[number]["after"]): boolean {
  if (!snapshot || file.kind !== snapshot.kind || file.revision !== snapshot.revision || file.size !== snapshot.size) return false;
  if (typeof file.content === "string" && typeof snapshot.content === "string") return file.content === snapshot.content;
  return file.mimeType === snapshot.mimeType;
}

export function planLegacyFileMigration(
  legacyFiles: readonly LegacyWorkspaceFile[],
  existingChats: readonly ChatSession[],
  createId: () => string = () => crypto.randomUUID(),
): { taskFiles: WorkspaceFile[]; createdChats: ChatSession[] } {
  const filesByProject = new Map<string, LegacyWorkspaceFile[]>();
  for (const file of legacyFiles) {
    const files = filesByProject.get(file.projectId) ?? [];
    files.push(file);
    filesByProject.set(file.projectId, files);
  }

  const taskFiles: WorkspaceFile[] = [];
  const createdChats: ChatSession[] = [];
  for (const [projectId, files] of filesByProject) {
    const currentByPath = new Map(files.map((file) => [file.path, file]));
    const candidates = existingChats.filter((chat) => chat.projectId === projectId).map((chat) => {
      const touched = new Set<string>();
      let exact = 0;
      let latestMutation = 0;
      for (const message of chat.messages) {
        for (const change of message.changes ?? []) {
          const current = currentByPath.get(change.path);
          if (!current) continue;
          touched.add(change.path);
          if (sameSnapshot(current, change.after)) exact += 1;
          latestMutation = Math.max(latestMutation, message.createdAt);
        }
      }
      return { chat, touched: touched.size, exact, latestMutation };
    });
    candidates.sort((left, right) => right.touched - left.touched || right.exact - left.exact
      || right.latestMutation - left.latestMutation || left.chat.createdAt - right.chat.createdAt);

    let owner = candidates[0]?.chat;
    if (!owner) {
      const createdAt = Math.min(...files.map((file) => file.createdAt));
      const updatedAt = Math.max(...files.map((file) => file.updatedAt));
      owner = { id: createId(), projectId, title: "Fichiers existants", model: DEFAULT_MODEL, effort: DEFAULT_REASONING_EFFORT,
        messages: [], apiInput: [], createdAt, updatedAt };
      createdChats.push(owner);
    }
    taskFiles.push(...files.map((file) => ({ ...file, chatId: owner.id })));
  }
  return { taskFiles, createdChats };
}

class CodexDatabase extends Dexie {
  projects!: EntityTable<Project, "id">;
  taskFiles!: Table<WorkspaceFile, [string, string]>;
  chats!: EntityTable<ChatSession, "id">;

  constructor() {
    super("codex-browser");
    this.version(1).stores({
      projects: "id, updatedAt, name",
      files: "[projectId+path], projectId, updatedAt",
      chats: "id, projectId, updatedAt",
    });
    this.version(2).stores({
      taskFiles: "[chatId+path], chatId, projectId, [projectId+chatId], updatedAt",
    }).upgrade(async (transaction) => {
      const legacyFiles = await transaction.table<LegacyWorkspaceFile, [string, string]>("files").toArray();
      if (!legacyFiles.length) return;
      const chats = await transaction.table<ChatSession, string>("chats").toArray();
      const migration = planLegacyFileMigration(legacyFiles, chats);
      if (migration.createdChats.length) await transaction.table<ChatSession, string>("chats").bulkAdd(migration.createdChats);
      if (migration.taskFiles.length) await transaction.table<WorkspaceFile, [string, string]>("taskFiles").bulkAdd(migration.taskFiles);
      await transaction.table("files").clear();
    });
    this.version(3).stores({ files: null });
  }
}

export const db = new CodexDatabase();

export async function createProject(name = "Nouveau projet", withStarter = true): Promise<Project> {
  const now = Date.now();
  const project: Project = { id: crypto.randomUUID(), name: name.trim() || "Nouveau projet", createdAt: now, updatedAt: now };
  const chat = createChatRecord(project.id, DEFAULT_MODEL, now);
  await db.transaction("rw", db.projects, db.chats, db.taskFiles, async () => {
    await db.projects.add(project);
    await db.chats.add(chat);
    if (withStarter) {
      const content = `# ${project.name}\n\nDécris ce que tu veux construire dans le chat.\n`;
      await db.taskFiles.add({ projectId: project.id, chatId: chat.id, path: "README.md", kind: "text", content,
        mimeType: "text/markdown", size: new Blob([content]).size, revision: 1, createdAt: now, updatedAt: now });
    }
  });
  return project;
}

export async function ensureFirstProject(): Promise<Project> {
  return db.transaction("rw", db.projects, db.chats, db.taskFiles, async () => {
    const latest = await db.projects.orderBy("updatedAt").last();
    if (latest) return latest;
    const now = Date.now();
    const project: Project = { id: crypto.randomUUID(), name: "Mon premier projet", createdAt: now, updatedAt: now };
    const chat = createChatRecord(project.id, DEFAULT_MODEL, now);
    const content = `# ${project.name}\n\nDécris ce que tu veux construire dans le chat.\n`;
    await db.projects.add(project);
    await db.chats.add(chat);
    await db.taskFiles.add({ projectId: project.id, chatId: chat.id, path: "README.md", kind: "text", content,
      mimeType: "text/markdown", size: new Blob([content]).size, revision: 1, createdAt: now, updatedAt: now });
    return project;
  });
}

export async function deleteProject(projectId: string): Promise<void> {
  await db.transaction("rw", db.projects, db.taskFiles, db.chats, async () => {
    await db.taskFiles.where("projectId").equals(projectId).delete();
    await db.chats.where("projectId").equals(projectId).delete();
    await db.projects.delete(projectId);
  });
}

export async function deleteChat(scope: WorkspaceScope): Promise<void> {
  await db.transaction("rw", db.taskFiles, db.chats, async () => {
    const chat = await db.chats.get(scope.chatId);
    if (!chat || chat.projectId !== scope.projectId) throw new Error("Tâche introuvable.");
    await db.taskFiles.where("chatId").equals(scope.chatId).delete();
    await db.chats.delete(scope.chatId);
  });
}

export async function touchProject(projectId: string): Promise<void> {
  await db.projects.update(projectId, { updatedAt: Date.now() });
}

export async function putTextFile(scope: WorkspaceScope, rawPath: string, content: string, expectedRevision?: number | null): Promise<WorkspaceFile> {
  const path = normalizeProjectPath(rawPath);
  return db.transaction("rw", db.taskFiles, db.projects, db.chats, async () => {
    const chat = await db.chats.get(scope.chatId);
    if (!chat || chat.projectId !== scope.projectId) throw new Error("Tâche introuvable.");
    const existing = await db.taskFiles.get([scope.chatId, path]);
    if (existing?.kind === "binary") throw new Error("Ce fichier est binaire et ne peut pas être remplacé par l’agent.");
    if (expectedRevision !== undefined) {
      const current = existing?.revision ?? null;
      if (current !== expectedRevision) throw new Error(`Conflit de version pour ${path} (attendu ${expectedRevision}, actuel ${current}).`);
    }
    const now = Date.now();
    const next: WorkspaceFile = { ...scope, path, kind: "text", content,
      mimeType: existing?.mimeType ?? guessMimeType(path), size: new Blob([content]).size,
      revision: (existing?.revision ?? 0) + 1, createdAt: existing?.createdAt ?? now, updatedAt: now };
    await db.taskFiles.put(next);
    await db.projects.update(scope.projectId, { updatedAt: now });
    await db.chats.update(scope.chatId, { updatedAt: now });
    return next;
  });
}

export function guessMimeType(path: string, fallback = "text/plain"): string {
  const extension = path.split(".").at(-1)?.toLowerCase();
  const types: Record<string, string> = {
    html: "text/html", htm: "text/html", css: "text/css", js: "text/javascript", mjs: "text/javascript",
    ts: "text/typescript", tsx: "text/typescript", jsx: "text/javascript", json: "application/json",
    md: "text/markdown", txt: "text/plain", xml: "application/xml", svg: "image/svg+xml",
    yml: "text/yaml", yaml: "text/yaml", csv: "text/csv", py: "text/x-python", rs: "text/x-rust",
    png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", gif: "image/gif", webp: "image/webp",
    avif: "image/avif", ico: "image/x-icon", bmp: "image/bmp",
    woff: "font/woff", woff2: "font/woff2", ttf: "font/ttf", otf: "font/otf", eot: "application/vnd.ms-fontobject",
    mp3: "audio/mpeg", wav: "audio/wav", ogg: "audio/ogg", mp4: "video/mp4", webm: "video/webm",
    pdf: "application/pdf", wasm: "application/wasm", webmanifest: "application/manifest+json", map: "application/json",
  };
  return types[extension ?? ""] ?? fallback;
}

export async function createChat(projectId: string, model = DEFAULT_MODEL): Promise<ChatSession> {
  const chat = createChatRecord(projectId, model);
  await db.chats.add(chat);
  return chat;
}

export async function ensureChat(projectId: string): Promise<ChatSession> {
  const chats = await db.chats.where("projectId").equals(projectId).sortBy("updatedAt");
  return chats.at(-1) ?? createChat(projectId);
}
